import { useState, useEffect, useCallback } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Video as VideoIcon,
  Music,
  Volume2,
  Lock,
  Globe,
  Users,
  UserCircle,
  FileText,
  Image,
  Link,
  WifiOff,
  Database,
  AlertCircle,
  Eye,
  BookOpen,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link as RouterLink } from "react-router-dom";
import { PhotoUploadPopup, VideoUploadPopup, StoryUploadPopup } from '.';
import { useMediaUpload, MEDIA_TYPES } from '../../hooks/useMediaUpload';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';
import { postService, storyService } from '@/lib/supabase-service';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import TransactionLog from './TransactionLog';

interface StatusBarProps {
  user: {
    id: string;
    username: string;
    avatar_url?: string;
    is_page?: boolean;
    is_music_page?: boolean;
  };
  onPostSubmit?: (content: string, mediaType: string, privacy: string) => void;
}

type PrivacyOption = {
  id: string;
  label: string;
  icon: React.ReactNode;
};

interface ConnectionStatus {
  isConnected: boolean;
  lastPing: Date | null;
  latency: number | null;
  error: string | null;
}

export default function StatusBar({ user, onPostSubmit }: StatusBarProps) {
  const [content, setContent] = useState('');
  const [selectedMediaType, setSelectedMediaType] = useState<string | null>(null);
  const { 
    uploadMultiple, 
    error: uploadError 
  } = useMediaUpload();
  const [selectedPrivacy, setSelectedPrivacy] = useState<string>(() => {
    // Get saved privacy setting from localStorage or default to "public"
    return localStorage.getItem('mozo-privacy-setting') || 'public';
  });
  
  // Missing state variables
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isPhotoPopupOpen, setIsPhotoPopupOpen] = useState(false);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const [isStoryPopupOpen, setIsStoryPopupOpen] = useState(false);
  
  // Debug state for connection monitoring
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    isConnected: false,
    lastPing: null,
    latency: null,
    error: null
  });
  const [showDebug, setShowDebug] = useState(() => {
    return localStorage.getItem('mozo-debug-mode') === 'true' || import.meta.env.DEV;
  });

  const { user: authUser } = useAuth();

  // Monitor Supabase connection status
  useEffect(() => {
    const checkConnection = async () => {
      const startTime = Date.now();
      try {
        const { error } = await supabase
          .from('users')
          .select('id')
          .limit(1);
        
        const latency = Date.now() - startTime;
        
        if (error) {
          setConnectionStatus({
            isConnected: false,
            lastPing: new Date(),
            latency,
            error: error.message
          });
        } else {
          setConnectionStatus({
            isConnected: true,
            lastPing: new Date(),
            latency,
            error: null
          });
        }
      } catch (err) {
        setConnectionStatus({
          isConnected: false,
          lastPing: new Date(),
          latency: Date.now() - startTime,
          error: err instanceof Error ? err.message : 'Unknown error'
        });
      }
    };

    // Initial check
    checkConnection();

    // Check every 10 seconds
    const interval = setInterval(checkConnection, 10000);

    return () => clearInterval(interval);
  }, []);

  // Save privacy setting to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('mozo-privacy-setting', selectedPrivacy);
  }, [selectedPrivacy]);

  // Determine avatar shape based on user type (page vs profile)
  const avatarShape = user.is_page ? "rounded-full" : "rounded-lg";

  // Privacy options
  const privacyOptions: PrivacyOption[] = [
    { id: 'public', label: 'Public', icon: <Globe className="h-4 w-4" /> },
    { id: 'followers', label: 'Followers', icon: <Users className="h-4 w-4" /> },
    { id: 'members', label: 'Members', icon: <UserCircle className="h-4 w-4" /> },
    { id: 'private', label: 'Only Me', icon: <Lock className="h-4 w-4" /> },
    { id: 'draft', label: 'Draft', icon: <FileText className="h-4 w-4" /> },
  ];

  // Get current privacy option
  const currentPrivacy = privacyOptions.find(option => option.id === selectedPrivacy) || privacyOptions[0];

  const handlePost = () => {
    if (onPostSubmit && (content.trim() || selectedMediaType)) {
      onPostSubmit(content, selectedMediaType || 'text', selectedPrivacy);
      setContent('');
      setSelectedMediaType(null);
    }
  };

  const handlePrivacyChange = (privacy: string) => {
    setSelectedPrivacy(privacy);
    setIsPrivacyOpen(false);
  };

  // State for transaction log entries
  const [transactionLog, setTransactionLog] = useState<Array<{
    id: string;
    timestamp: Date;
    type: 'info' | 'success' | 'error' | 'warning';
    message: string;
    details?: any;
  }>>([]);

  // Function to add a log entry
  const addLogEntry = useCallback((type: 'info' | 'success' | 'error' | 'warning', message: string, details?: any) => {
    setTransactionLog(prev => [
      {
        id: uuidv4(),
        timestamp: new Date(),
        type,
        message,
        details
      },
      ...prev.slice(0, 19) // Keep only the last 20 entries
    ]);
  }, []);

  // Enhanced logging functions that add to both console and UI
  const logInfo = useCallback((message: string, details?: any) => {
    console.log(`ℹ️ ${message}`, details || '');
    addLogEntry('info', message, details);
  }, [addLogEntry]);

  const logSuccess = useCallback((message: string, details?: any) => {
    console.log(`✅ ${message}`, details || '');
    addLogEntry('success', message, details);
  }, [addLogEntry]);

  const logError = useCallback((message: string, details?: any) => {
    console.error(`❌ ${message}`, details || '');
    addLogEntry('error', message, details);
  }, [addLogEntry]);

  // Clear log
  const clearLog = useCallback(() => {
    setTransactionLog([]);
  }, []);

  // Show transaction log state
  const [showTransactionLog, setShowTransactionLog] = useState(true);

  // Handle photo upload submission
  const handlePhotoUploadSubmit = async (photoData: any) => {
    logInfo('Photo upload submission started', photoData);
    
    try {
      // Check if user is authenticated
      if (!authUser) {
        logError('User not authenticated');
        toast.error('You must be logged in to upload photos');
        return;
      }
      
      logInfo('Current user', authUser);
      
      // Upload photos to storage first
      const uploadPromises = photoData.photos.map(async (photo: any) => {
        logInfo('Uploading photo', { id: photo.id, fileName: photo.file?.name });
        
        if (!photo.file) {
          logError('Photo file is missing', photo);
          throw new Error('Photo file is missing');
        }
        
        const uploadResult = await uploadMultiple([photo.file], MEDIA_TYPES.POST_IMAGE);
        
        if (uploadResult.length > 0 && uploadResult[0].success) {
          logSuccess('Photo uploaded successfully', uploadResult[0]);
          return {
            url: uploadResult[0].url,
            type: 'image',
            fileName: photo.file.name,
            fileSize: photo.file.size,
            fileType: photo.file.type
          };
        } else {
          logError('Photo upload failed', uploadResult[0]?.error);
          throw new Error(`Photo upload failed: ${uploadResult[0]?.error}`);
        }
      });
      
      // Wait for all uploads to complete
      const mediaUrls = await Promise.all(uploadPromises);
      logInfo('All photos uploaded successfully', mediaUrls);
      
      // Test database connection before attempting to create post
      try {
        const { error: connectionError } = await supabase
          .from('users')
          .select('id')
          .limit(1);
          
        if (connectionError) {
          logError('Database connection test failed', connectionError);
          toast.error('Connection error. Please try again.');
          return;
        }
        
        logSuccess('Database connection test succeeded');
      } catch (connectionTestError) {
        logError('Database connection test error', connectionTestError);
        toast.error('Connection error. Please try again.');
        return;
      }
      
      // Check if user profile exists, create if missing
      try {
        const { data: existingProfile, error: profileCheckError } = await supabase
          .from('users')
          .select('id')
          .eq('id', authUser.id)
          .single();
          
        if (profileCheckError && profileCheckError.code === 'PGRST116') {
          // Profile doesn't exist, create it
          logInfo('User profile not found, creating profile', { userId: authUser.id });
          
          const { error: createProfileError } = await supabase
            .from('users')
            .insert([
              {
                id: authUser.id,
                username: authUser.user_metadata?.username || `user_${Math.floor(Math.random() * 10000)}`,
                full_name: authUser.user_metadata?.full_name || '',
                email: authUser.email,
                avatar_url: authUser.user_metadata?.avatar_url || '',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }
            ]);
            
          if (createProfileError) {
            logError('Failed to create user profile', createProfileError);
            toast.error('Failed to create user profile. Please try again.');
            return;
          }
          
          logSuccess('User profile created successfully');
        } else if (profileCheckError) {
          logError('Error checking user profile', profileCheckError);
          toast.error('Profile check failed. Please try again.');
          return;
        } else {
          logInfo('User profile exists', existingProfile);
        }
      } catch (profileError) {
        logError('Profile creation error', profileError);
        toast.error('Profile error. Please try again.');
        return;
      }
      
      // Create post data
      const postData = {
        user_id: authUser.id,
        content: photoData.description || '',
        media_url: mediaUrls.length > 0 ? mediaUrls[0].url : '', // Use first image URL as primary media_url
        media_urls: JSON.stringify(mediaUrls), // Add all media URLs as JSON array
        media_type: 'image', // Fix: use 'image' instead of 'photo' to match database schema
        type: 'image', // Fix: use 'image' instead of 'photo' to match database constraint
        location: photoData.location || null,
        privacy: photoData.privacy || 'public', // Use privacy from photoData
        metrics: { likes: 0, comments: 0, shares: 0, views: 0 }
      };
      
      logInfo('Post data to create', postData);
      
      // Create post in database
      const post = await postService.createPost(postData);
      logSuccess('Post created successfully', post);
      
      // Show success message
      toast.success('Photo uploaded successfully!');
      
      // Close popup
      setIsPhotoPopupOpen(false);
      
    } catch (error: any) {
      logError('Error creating post', error);
      
      // Check for specific error types
      if (error.message?.includes('foreign key constraint')) {
        logError('Foreign key constraint violation. Check user_id reference.');
        toast.error('Post creation failed: User profile issue');
      } else if (error.message?.includes('column')) {
        logError('Column error. Database schema may be outdated.');
        toast.error('Post creation failed: Database schema issue');
      } else {
        toast.error('Failed to create post. Please try again.');
      }
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      {/* Debug Panel */}
      {showDebug && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-4 p-4">
              {/* Connection Status */}
              <div className="flex items-center space-x-2">
                {connectionStatus.isConnected ? (
                  <div className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                    <Database className="w-3 h-3" />
                    <span>Connected</span>
                    {connectionStatus.latency && (
                      <span className="text-gray-500">({connectionStatus.latency}ms)</span>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 text-red-600 dark:text-red-400">
                    <WifiOff className="w-3 h-3" />
                    <span>Disconnected</span>
                  </div>
                )}
              </div>
            ) : (
              {/* Upload Error */}
              {uploadError && (
                <div className="flex items-center space-x-1 text-red-600 dark:text-red-400">
                  <AlertCircle className="w-3 h-3" />
                  <span>Upload Error: {uploadError}</span>
                </div>
              )}

              {/* Connection Error */}
              {connectionStatus.error && (
                <div className="flex items-center space-x-1 text-orange-600 dark:text-orange-400">
                  <AlertCircle className="w-3 h-3" />
                  <span>DB Error: {connectionStatus.error}</span>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2 text-gray-500">
              {connectionStatus.lastPing && (
                <span>Last ping: {connectionStatus.lastPing.toLocaleTimeString()}</span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const newDebugState = !showDebug;
                  setShowDebug(newDebugState);
                  localStorage.setItem('mozo-debug-mode', newDebugState.toString());
                }}
                className="h-6 px-2 text-xs"
              >
                {showDebug ? 'Hide Debug' : 'Show Debug'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-4 mb-4">
        {/* Line 1: Avatar, Input, Post button */}
        <div className="flex items-center gap-3 mb-3">
          <RouterLink to={`/profile/${user.username}`}>
            <Avatar className={`h-10 w-10 ${avatarShape} cursor-pointer`}>
              <AvatarImage src={user.avatar_url} alt={user.username} />
              <AvatarFallback>
                <img
                  src={`https://ui-avatars.com/api/?name=${user.username}&background=random&color=fff`}
                  alt={user.username}
                  className="w-full h-full object-cover"
                />
              </AvatarFallback>
            </Avatar>
          </RouterLink>

          <div className="flex-1 relative">
            <Popover open={isPrivacyOpen} onOpenChange={setIsPrivacyOpen}>
              <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-md">
                <PopoverTrigger asChild>
                  <button
                    className="flex items-center gap-1 px-2 py-1 hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white rounded-l-md transition-colors duration-200"
                    aria-label="Select privacy"
                  >
                    {currentPrivacy.icon}
                    <span className="text-xs text-gray-600 dark:text-gray-300 group-hover:text-white">{currentPrivacy.label}</span>
                  </button>
                </PopoverTrigger>

                <Input
                  className="flex-1 bg-transparent border-0 focus-visible:ring-1 focus-visible:ring-[#065900]"
                  placeholder={`What's on your mind, ${user.username}?`}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                />
              </div>

              <PopoverContent className="w-48 p-1">
                <div className="flex flex-col space-y-1">
                  {privacyOptions.map((option) => (
                    <button
                      key={option.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${selectedPrivacy === option.id ? 'bg-[#065900] text-white' : 'hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white'}`}
                      onClick={() => handlePrivacyChange(option.id)}
                    >
                      {option.icon}
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <Button
            className="bg-[#065900] hover:bg-[#054800] text-white"
            onClick={handlePost}
            disabled={!content.trim() && !selectedMediaType}
          >
            Post
          </Button>
        </div>

        {/* Line 2: Media type icons */}
        <div className="flex items-center justify-around border-t pt-3 dark:border-gray-700">
          <button
            className={`flex items-center gap-1 px-3 py-1 rounded-md transition-colors duration-200 ${selectedMediaType === 'story' ? 'bg-[#065900] text-white' : 'hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white'
              }`}
            onClick={() => { setSelectedMediaType('story'); setIsStoryPopupOpen(true); }}
          >
            <BookOpen className="h-5 w-5" />
            <span className="text-sm">Story</span>
          </button>

          <button
            className={`flex items-center gap-1 px-3 py-1 rounded-md transition-colors duration-200 ${selectedMediaType === 'photo' ? 'bg-[#065900] text-white' : 'hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white'
              }`}
            onClick={() => { setSelectedMediaType('photo'); setIsPhotoPopupOpen(true); }}
          >
            <Image className="h-5 w-5" />
            <span className="text-sm">Photo</span>
          </button>

          <button
            className={`flex items-center gap-1 px-3 py-1 rounded-md transition-colors duration-200 ${selectedMediaType === 'video' ? 'bg-[#065900] text-white' : 'hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white'
              }`}
            onClick={() => { setSelectedMediaType('video'); setIsVideoPopupOpen(true); }}
          >
            <VideoIcon className="h-5 w-5" />
            <span className="text-sm">Video</span>
          </button>

          {user.is_page && user.is_music_page ? (
            <button
              className={`flex items-center gap-1 px-3 py-1 rounded-md transition-colors duration-200 ${selectedMediaType === 'music' ? 'bg-[#065900] text-white' : 'hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white'
                }`}
              onClick={() => { setSelectedMediaType(selectedMediaType === 'music' ? null : 'music'); /* TODO: Implement Music Popup if available */ }}
            >
              <Music className="h-5 w-5" />
              <span className="text-sm">Music</span>
            </button>
          ) : (
            <>
              <button
                className={`flex items-center gap-1 px-3 py-1 rounded-full ${selectedMediaType === 'sound' ? 'bg-[#065900]/10 text-[#065900]' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                onClick={() => { setSelectedMediaType(selectedMediaType === 'sound' ? null : 'sound'); /* TODO: Implement Sound Popup if available */ }}
              >
                <Volume2 className="h-5 w-5" />
                <span className="text-sm">Sound</span>
              </button>
              
              <button
                className={`flex items-center gap-1 px-3 py-1 rounded-full ${selectedMediaType === 'link' ? 'bg-[#065900]/10 text-[#065900]' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                onClick={() => { setSelectedMediaType(selectedMediaType === 'link' ? null : 'link'); /* TODO: Implement LinkEm Popup if available */ }}
              >
                <Link className="h-5 w-5" />
                <span className="text-sm">LinkEm</span>
              </button>
            </>
          )}
        </div>

        {isPhotoPopupOpen && (
          <PhotoUploadPopup
            isOpen={isPhotoPopupOpen}
            onClose={() => setIsPhotoPopupOpen(false)}
            onSubmit={handlePhotoUploadSubmit}
          />
        )}
        {isVideoPopupOpen && (
          <VideoUploadPopup
            isOpen={isVideoPopupOpen}
            onClose={() => {
              setIsVideoPopupOpen(false);
              setSelectedMediaType(null);
            }}
            onSubmit={async (data) => {
              try {
                logInfo('Video upload submission started', data);
                
                // Debug: Log the file object details
                console.log('🎬 StatusBar video upload - received data:', {
                  hasFile: !!data.file,
                  fileName: data.file?.name,
                  fileSize: data.file?.size,
                  fileType: data.file?.type,
                  fileObject: data.file,
                  dataKeys: Object.keys(data),
                  fullData: data
                });
                
                // Check if user is authenticated
                if (!authUser) {
                  logError('User not authenticated');
                  toast.error('You must be logged in to upload videos');
                  return;
                }
                
                // Debug: Log before upload
                console.log('📤 StatusBar about to call uploadMultiple with:', {
                  fileArray: [data.file],
                  hasFileInArray: data.file !== undefined && data.file !== null,
                  fileDetails: data.file ? {
                    name: data.file.name,
                    size: data.file.size,
                    type: data.file.type
                  } : 'NO FILE'
                });
                
                // Upload the video file to Supabase storage
                const uploadResult = await uploadMultiple(
                  [data.file], 
                  MEDIA_TYPES.POST_VIDEO
                );
                
                if (uploadResult.length > 0 && uploadResult[0].success) {
                  logSuccess('Video uploaded successfully', uploadResult[0]);
                  
                  // Test database connection before attempting to create post
                  try {
                    const { error: connectionError } = await supabase
                      .from('users')
                      .select('id')
                      .limit(1);
                      
                    if (connectionError) {
                      logError('Database connection test failed', connectionError);
                      toast.error('Connection error. Please try again.');
                      return;
                    }
                    
                    logSuccess('Database connection test succeeded');
                  } catch (connectionTestError) {
                    logError('Database connection test error', connectionTestError);
                    toast.error('Connection error. Please try again.');
                    return;
                  }
                  
                  // Check if user profile exists, create if missing
                  try {
                    const { data: existingProfile, error: profileCheckError } = await supabase
                      .from('users')
                      .select('id')
                      .eq('id', authUser.id)
                      .single();
                      
                    if (profileCheckError && profileCheckError.code === 'PGRST116') {
                      // Profile doesn't exist, create it
                      logInfo('User profile not found, creating profile', { userId: authUser.id });
                      
                      const { error: createProfileError } = await supabase
                        .from('users')
                        .insert([
                          {
                            id: authUser.id,
                            username: authUser.user_metadata?.username || `user_${Math.floor(Math.random() * 10000)}`,
                            full_name: authUser.user_metadata?.full_name || '',
                            email: authUser.email,
                            avatar_url: authUser.user_metadata?.avatar_url || '',
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString()
                          }
                        ]);
                        
                      if (createProfileError) {
                        logError('Failed to create user profile', createProfileError);
                        toast.error('Failed to create user profile. Please try again.');
                        return;
                      }
                      
                      logSuccess('User profile created successfully');
                    } else if (profileCheckError) {
                      logError('Error checking user profile', profileCheckError);
                      toast.error('Profile check failed. Please try again.');
                      return;
                    } else {
                      logInfo('User profile exists', existingProfile);
                    }
                  } catch (profileError) {
                    logError('Profile creation error', profileError);
                    toast.error('Profile error. Please try again.');
                    return;
                  }
                  
                  // Create video post data
                  const videoPost = {
                    user_id: authUser.id,
                    content: data.description || '',
                    media_url: uploadResult[0].url,
                    media_type: 'video',
                    type: 'video',
                    location: data.location || null,
                    privacy: data.privacy || 'public',
                    metrics: { likes: 0, comments: 0, shares: 0, views: 0 }
                  };
                  
                  logInfo('Video post data', videoPost);
                  
                  // Create post in database
                  const post = await postService.createPost(videoPost);
                  logSuccess('Video post created successfully', post);
                  
                  // Show success message
                  toast.success('Video uploaded successfully!');
                } else {
                  logError('Video upload failed', uploadResult[0]?.error);
                  toast.error('Video upload failed. Please try again.');
                }
              } catch (error) {
                logError('Error uploading video', error);
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                toast.error(`Failed to upload video: ${errorMessage}`);
              } finally {
                setIsVideoPopupOpen(false);
                setSelectedMediaType(null);
              }
            }}
          />
        )}
        {isStoryPopupOpen && (
          <StoryUploadPopup
            isOpen={isStoryPopupOpen}
            onClose={() => setIsStoryPopupOpen(false)}
            onSubmit={async (file: File, data: {
              description?: string;
              location?: string;
              privacy?: 'public' | 'friends' | 'private';
            }) => {
              try {
                logInfo('Story upload submission started', { file, ...data });

                // Debug: Log the file object details
                console.log('📚 StatusBar story upload - received data:', {
                  hasFile: !!file,
                  fileName: file?.name,
                  fileSize: file?.size,
                  fileType: file?.type,
                  fileObject: file,
                  dataKeys: Object.keys(data),
                  fullData: { file, ...data },
                });

                // Check if user is authenticated
                if (!authUser) {
                  logError('User not authenticated');
                  toast.error('You must be logged in to upload stories');
                  return;
                }

                // Debug: Log before upload
                console.log('📤 StatusBar about to call uploadMultiple with:', {
                  fileArray: [file],
                  hasFileInArray: file !== undefined && file !== null,
                  fileDetails: file
                    ? {
                        name: file.name,
                        size: file.size,
                        type: file.type,
                      }
                    : 'NO FILE',
                });

                // Upload the story file to Supabase storage
                const uploadResult = await uploadMultiple(
                  [file],
                  MEDIA_TYPES.STORY
                );

                if (uploadResult.length > 0 && uploadResult[0].success) {
                  logSuccess('Story uploaded successfully', uploadResult[0]);

                  // Test database connection before attempting to create post
                  try {
                    const { error: connectionError } = await supabase
                      .from('users')
                      .select('id')
                      .limit(1);

                    if (connectionError) {
                      logError('Database connection test failed', connectionError);
                      toast.error('Connection error. Please try again.');
                      return;
                    }

                    logSuccess('Database connection test succeeded');
                  } catch (connectionTestError) {
                    logError('Database connection test error', connectionTestError);
                    toast.error('Connection error. Please try again.');
                    return;
                  }

                  // Check if user profile exists, create if missing
                  try {
                    const { data: existingProfile, error: profileCheckError } = await supabase
                      .from('users')
                      .select('id')
                      .eq('id', authUser.id)
                      .single();

                    if (profileCheckError && profileCheckError.code === 'PGRST116') {
                      // Profile doesn't exist, create it
                      logInfo('User profile not found, creating profile', { userId: authUser.id });

                      const { error: createProfileError } = await supabase
                        .from('users')
                        .insert([
                          {
                            id: authUser.id,
                            username: authUser.user_metadata?.username || `user_${Math.floor(Math.random() * 10000)}`,
                            full_name: authUser.user_metadata?.full_name || '',
                            email: authUser.email,
                            avatar_url: authUser.user_metadata?.avatar_url || '',
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                          },
                        ]);

                      if (createProfileError) {
                        logError('Failed to create user profile', createProfileError);
                        toast.error('Failed to create user profile. Please try again.');
                        return;
                      }

                      logSuccess('User profile created successfully');
                    } else if (profileCheckError) {
                      logError('Error checking user profile', profileCheckError);
                      toast.error('Profile check failed. Please try again.');
                      return;
                    } else {
                      logInfo('User profile exists', existingProfile);
                    }
                  } catch (profileError) {
                    logError('Profile creation error', profileError);
                    toast.error('Profile error. Please try again.');
                    return;
                  }

                  // Create story data
                  const storyData = {
                    user_id: authUser.id,
                    caption: data.description || '',
                    media_url: uploadResult[0].filePath,
                    media_type: file.type?.startsWith('image/') ? 'image' : 'video',
                    location: data.location || null,
                    privacy: data.privacy || 'public',
                    expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours from now
                  };

                  logInfo('Story data', storyData);

                  // Create story in database
                  const story = await storyService.createStory(storyData);
                  logSuccess('Story created successfully', story);

                  // Show success message
                  toast.success('Story uploaded successfully!');
                } else {
                  logError('Story upload failed', uploadResult[0]?.error);
                  toast.error('Story upload failed. Please try again.');
                }
              } catch (error) {
                logError('Error uploading story', error);
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                toast.error(`Failed to upload story: ${errorMessage}`);
              } finally {
                setIsStoryPopupOpen(false);
                setSelectedMediaType(null);
              }
            }}
          />
        )}
      </div>
      
      {/* Transaction Log Panel */}
      {showTransactionLog && transactionLog.length > 0 && (
        <TransactionLog
          entries={transactionLog}
          onClear={clearLog}
          onHide={() => setShowTransactionLog(false)}
        />
      )}
      
      {/* Show Transaction Log Button (when hidden) */}
      {!showTransactionLog && transactionLog.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-2 text-center">
          <button
            onClick={() => setShowTransactionLog(true)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-2 mx-auto"
          >
            <Eye className="h-4 w-4" />
            Show Transaction Log ({transactionLog.length})
          </button>
        </div>
      )}
    </div>
  );
}
